import clsx from 'clsx'
import LayoutContainer from 'components/Common/LayoutContainer'
import Pane from 'components/Common/Pane'
import { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import * as React from 'react'
import styled from 'styled-components'
import { rem } from 'polished'
import VerticalSpacer from 'components/Common/Spacing/VerticalSpacer'
import useGlobalSearchVerticalTabs from 'hooks/GlobalSearch/useGlobalSearchVerticalTabs'

import HorizontalContainerWithFadedOverflow from 'components/Common/HorizontalContainerWithFadedOverflow'
import FilterChip from 'LuxKit/Chips/FilterChip'
import useGlobalSearchURLHashVertical from 'hooks/GlobalSearch/useGlobalSearchURLHashVertical'
import LineMultiplyIcon from 'LuxKit/Icons/line/LineMultiplyIcon'
import IconButton from 'LuxKit/Button/IconButton'
import Backdrop from 'components/utils/Backdrop'
import Group from 'components/utils/Group'
import Typography from 'LuxKit/Typography/Typography'
import { useScreenSizeOnly } from 'hooks/useScreenSize'
import { useElementWidth } from 'hooks/useElementSize'
import zIndex from 'styles/tools/z-index'
import { mediaQueryUp } from 'components/utils/breakpoint'
import useIsSearchPage from 'hooks/Navigation/useIsSearchPage'
import { matchPath, useLocation } from 'react-router'
import { capitalise } from 'lib/string/stringUtils'
import GlobalSearchLoyaltyBurnToggle from 'components/Search/GlobalSearch/GlobalSearchLoyaltyBurnToggle'

const animationDuration = '0.5s'

const NavSearchContainer = styled.div`
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  width: 100%;
  pointer-events: none;
  z-index: ${zIndex.menu};
  opacity: 1;
  transition: opacity;
  transition-duration: 0s;
  transition-timing-function: linear;

  &.closed {
    opacity: 0;
    transition-duration: 0.2s;
  }
`

const AnimatedSearchBarSheet = styled(Pane)`
  position: absolute;
  left: 0;
  right: 0;
  top: ${rem(32)};
  opacity: 1;
  height: ${rem(64)};
  min-height: ${rem(64)};
  width: var(--input-width);
  left: var(--input-left);
  transition: top, left, height, min-height, width, opacity, border-radius;
  transition-delay: calc(${animationDuration} * 0.25);
  transition-duration: calc(${animationDuration} * 0.10);
  transition-timing-function: ease-in;
  z-index: -1;
  border-radius: 99px;

  &.open {
    transition: top, left, height, min-height, width, opacity, border-radius;
    top: 0;
    opacity: 1;
    left: 0;
    width: 100%;
    min-height: ${rem(148)};
    height: 100%;
    transition-delay: 0s;
    border-radius: 0px;
    transition-duration: calc(${animationDuration} * 0.33);
    transition-timing-function: linear;
  }
`

const AnimatedSearchInput = styled.div`
  position: absolute;
  top: 0;
  left: var(--input-left);
  width: 100%;
  max-width: var(--input-width);
  transform: translateY(${rem(44)});
  height: ${rem(40)};
  z-index: 10;
  opacity: 1;
  pointer-events: none;
  border: 1px solid ${props => props.theme.palette.neutral.surface.lightForeground};
  background-color: ${props => props.theme.palette.neutral.surface.lightBackground};
  border-radius: ${props => props.theme.borderRadius.S};
  align-items: center;
  padding: ${rem(8)} ${rem(16)};

  transition: transform calc(${animationDuration} * 0.25) calc(${animationDuration} * 0.10),
    left calc(${animationDuration} * 0.25) calc(${animationDuration} * 0.10),
    height calc(${animationDuration} * 0.25) calc(${animationDuration} * 0.10),
    max-width calc(${animationDuration} * 0.25) calc(${animationDuration} * 0.10),
    opacity calc(${animationDuration} * 0.25);

    transition-timing-function: ease-in;

  &.open {
    transform: translateY(var(--input-y-position));
    left: max(calc(((100vw - var(--scrollbar-width, 0px) - ${rem(1140)}) / 2)), ${rem(16)});
    height: ${rem(50)};
    max-width: min(${rem(1140)}, calc(100vw - ${rem(32)}));
    opacity: 0;

    transition: transform calc(${animationDuration} * 0.33),
    left calc(${animationDuration} * 0.33),
    height calc(${animationDuration} * 0.33),
    max-width calc(${animationDuration} * 0.33),
    opacity calc(${animationDuration} * 0.33) calc(${animationDuration} * 0.25);
    transition-timing-function: linear;
  }
`

const SearchContainer = styled(Pane)`
  opacity: 0;
  transition: opacity calc(${animationDuration} * 0.15);
  transition-timing-function: ease-in;

  &.open {
    opacity: 1;
    transition: opacity calc(${animationDuration} * 0.33) calc(${animationDuration} * 0.25);
    pointer-events: auto;
    transition-timing-function: linear;
  }
`

const ChipContainer = styled(Group)`
  opacity: 0;
  transition: opacity calc(${animationDuration} * 0.10);
  transition-timing-function: ease-in;

  & > * {
    transition: opacity calc(${animationDuration} * 0.10);
    transition-timing-function: ease-in;
    opacity: calc(0.7 - calc(var(--chip-index) * 0.1));
  }

  &.open {
    opacity: 1;
    transition: opacity calc(${animationDuration} * 0.59) calc(${animationDuration} * 0.41);
    transition-timing-function: linear;

    & > * {
      opacity: 1;
      transition-timing-function: linear;
    }
  }
`

const CloseIcon = styled(IconButton)`
  position: absolute;
  top: ${rem(12)};
  right: ${rem(12)};
  display: none;

  ${mediaQueryUp.desktop} {
    display: inline-flex;
  }
`

const SearchBackdrop = styled(Backdrop)`
  z-index: ${zIndex.menu - 1};
`

const verticalSearchPositions: Record<App.SearchVertical, string> = {
  'car-hire': rem(64),
  'homes-and-villas': rem(64),
  cruises: rem(64),
  experiences: rem(64),
  flights: rem(120),
  hotels: rem(64),
  tours: rem(64),
  merchandise: rem(64),
}

interface Props {
  className?: string
  searchInputRef?: React.RefObject<HTMLButtonElement | null>
}

function NavBarSearch(props: Props) {
  const { className, searchInputRef } = props
  const location = useLocation()
  const { openedSearchVertical, toggleSearchVertical } = useGlobalSearchURLHashVertical()
  const { pathname: locationPathname, search: locationSearch } = location

  const searchVerticals = useGlobalSearchVerticalTabs()
  const searchTab = openedSearchVertical ? searchVerticals.get(openedSearchVertical) : undefined
  const [savedActiveTab, setSavedActiveTab] = useState<App.GlobalSearchVerticalTab | undefined>(searchTab)
  const [animating, setAnimating] = useState<boolean>(false)
  const searchComponentRef = useRef<HTMLDivElement>(null)
  const previousLocationRef = useRef<{ pathname: string; search: string } | null>(null)

  const searchInputWidth = useElementWidth(searchInputRef)
  const onVerticalClick = useCallback<React.MouseEventHandler<HTMLButtonElement>>((e) => {
    const vertical = e.currentTarget.dataset.vertical as App.SearchVertical
    toggleSearchVertical(vertical)
  }, [toggleSearchVertical])

  const onClose = useCallback(() => toggleSearchVertical('none'), [toggleSearchVertical])

  // Mobile uses a modal, so we're not doing anything here
  const isMobile = useScreenSizeOnly('mobile')
  const startOpen = !isMobile && !!openedSearchVertical
  const open = startOpen && animating

  useEffect(() => {
    if (startOpen) {
      // To make the transitions apply, we need to
      setAnimating(true)
    }
  }, [startOpen])

  // Ensure the global search overlay closes when navigating to a new route (e.g., search results)
  // Only applies to desktop - mobile uses a modal that manages its own close behavior
  useEffect(() => {
    if (isMobile) return

    const previousLocation = previousLocationRef.current
    const hasNavigated = previousLocation ?
      previousLocation.pathname !== locationPathname || previousLocation.search !== locationSearch :
      false

    previousLocationRef.current = { pathname: locationPathname, search: locationSearch }

    if (hasNavigated && openedSearchVertical) {
      toggleSearchVertical('none')
    }
  }, [isMobile, locationPathname, locationSearch, openedSearchVertical, toggleSearchVertical])

  useEffect(() => {
    if (open) {
      // find the first input on the currently shown search form and focus it as we open
      const mainInput = searchComponentRef.current?.querySelector<HTMLInputElement>('input[type="text"]:not(input[inputMode="none"]')
      mainInput?.focus()
    }
  }, [open])

  useEffect(() => {
    if (searchTab) {
      setSavedActiveTab(searchTab)
    }
  }, [searchTab])

  const isSearchPage = useIsSearchPage()

  const hideSearchBar = useMemo(() => {
    // all search pages except the merchandise one provide their own search bars
    const isMerchandiseSearchPage = matchPath(location.pathname, { path: '/:regionCode/search/shop' })

    return isSearchPage && !isMerchandiseSearchPage && !searchInputRef?.current
  }, [isSearchPage, searchInputRef, location.pathname])

  useEffect(() => {
    if (hideSearchBar) {
      // clean up any animating if we're on a search page. It'll be unmounted anyway
      setAnimating(false)
    }
  }, [hideSearchBar])

  const onTransitionEnd = useCallback<React.TransitionEventHandler<HTMLDivElement>>(() => {
    if (!openedSearchVertical) {
      setAnimating(false)
    }
  }, [openedSearchVertical])

  if (hideSearchBar) {
    // search doesn't need another search bar, it handles it's own
    return null
  }

  // we need to keep the tab component around as we're closing so we can animate it out
  const activeTab = searchTab ?? savedActiveTab

  const showPointsToggle = activeTab?.id === 'hotels' || activeTab?.id === 'homes-and-villas'

  return <>
    <NavSearchContainer
      className={clsx(className, { closed: animating ? false : !openedSearchVertical })}
      style={{
        '--input-y-position': activeTab ? verticalSearchPositions[activeTab?.id] : 0,
        '--input-width': searchInputWidth ? rem(searchInputWidth + 34) : undefined,
        '--input-left': searchInputRef?.current?.offsetLeft ? rem(searchInputRef.current.offsetLeft) : undefined,
      }}
    >
      <AnimatedSearchBarSheet className={clsx({ open })} onTransitionEnd={onTransitionEnd} />
      <AnimatedSearchInput className={clsx({ open })} />
      <SearchContainer padding="24 0 16 0" className={clsx({ open })}>
        <CloseIcon
          kind="tertiary"
          size="large"
          onClick={onClose}
        >
          <LineMultiplyIcon />
        </CloseIcon>
        <LayoutContainer>
          <VerticalSpacer gap={12}>
            <HorizontalContainerWithFadedOverflow overflowGutter={8}>
              <ChipContainer direction="horizontal" gap={8} className={clsx({ open })}>
                {searchVerticals.entries().map(([verticalType, vertical], index) => <FilterChip
                  key={vertical.label}
                  size="small"
                  onClick={onVerticalClick}
                  data-vertical={verticalType}
                  selected={verticalType === activeTab?.id}
                  startIcon={<vertical.Icon />}
                  style={{ '--chip-index': index }}
                >
                  <Typography as="span" format="titlecase">{capitalise(vertical.label)}</Typography>
                </FilterChip>).toArray()}
              </ChipContainer>
            </HorizontalContainerWithFadedOverflow>
            <div ref={searchComponentRef}>
              {open && !!activeTab && <activeTab.Component />}
            </div>
            {open && showPointsToggle && <GlobalSearchLoyaltyBurnToggle />}
          </VerticalSpacer>

        </LayoutContainer>
      </SearchContainer>
    </NavSearchContainer>
    <SearchBackdrop visible={open} onClick={onClose} data-testid="global-search-backdrop" />
  </>
}

export default React.memo(NavBarSearch)
